/**
 *    SPDX-License-Identifier: Apache-2.0
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Button } from 'reactstrap';
import matchSorter from 'match-sorter';
import moment from 'moment';
import ReactTable from '../Styled/Table';
import DatePicker from '../Styled/DatePicker';
import MultiSelect from '../Styled/MultiSelect';
import { TablePagination } from '@mui/material';

import {
	currentChannelType,
	getTransactionType,
	transactionListType,
	transactionType
} from '../types';

/* istanbul ignore next */
const styles = theme => {
	const { type } = theme.palette;
	const dark = type === 'dark';
	return {
		container: {
			padding: '12px',
			backgroundColor: dark ? '#2a2438' : '#ffffff'
		},
		filterSection: {
			backgroundColor: dark ? '#3c3558' : '#ffffff',
			borderRadius: '4px',
			padding: '12px',
			marginBottom: '12px',
			border: dark ? '1px solid #4a4a4a' : '1px solid #e0e0e0'
		},
		filterTitle: {
			fontSize: '14px',
			fontWeight: '600',
			marginBottom: '12px',
			color: dark ? '#ffffff' : '#333333',
			paddingBottom: '8px',
			borderBottom: `1px solid ${dark ? '#4a4a4a' : '#e0e0e0'}`
		},
		hash: {
			'&, & li': {
				overflow: 'visible !important'
			}
		},
		transactionLink: {
			color: dark ? '#4A90E2' : '#4A90E2',
			textDecoration: 'none',
			fontWeight: '500',
			cursor: 'pointer',
			'&:hover': {
				textDecoration: 'underline',
				color: dark ? '#6bb3ff' : '#2d6fb8'
			}
		},
		fullHash: {
			display: 'none'
		},
		lastFullHash: {},
		filter: {
			width: '100%',
			margin: '0px !important',
			display: 'flex',
			alignItems: 'flex-end',
			flexWrap: 'wrap'
		},
		filterButton: {
			width: '100% !important',
			borderRadius: '4px',
			fontWeight: '400',
			padding: '8px 12px',
			fontSize: '13px',
			height: '38px',
			lineHeight: '1.2'
		},
		searchButton: {
			width: '100% !important',
			backgroundColor: dark ? '#4A90E2' : '#4A90E2',
			borderColor: dark ? '#4A90E2' : '#4A90E2',
			borderRadius: '4px',
			fontWeight: '400',
			padding: '8px 12px',
			fontSize: '13px',
			height: '38px',
			lineHeight: '1.2',
			'&:hover': {
				backgroundColor: dark ? '#6bb3ff' : '#2d6fb8'
			},
			'&:disabled': {
				opacity: 0.5,
				cursor: 'not-allowed'
			}
		},
		filterElement: {
			textAlign: 'left',
			display: 'flex',
			flexDirection: 'column',
			padding: '0px 8px !important',
			justifyContent: 'flex-end',
			'& > div': {
				width: '100% !important'
			},
			'& .label': {
				margin: '0 0 6px 0',
				fontWeight: '400',
				fontSize: '12px',
				color: dark ? '#e0e0e0' : '#666666',
				height: '18px',
				lineHeight: '18px',
				display: 'block'
			},
			'& input, & .react-datepicker-wrapper, & .react-datepicker__input-container': {
				width: '100% !important',
				'& input': {
					height: '38px !important',
					padding: '8px 12px !important',
					fontSize: '13px !important',
					borderRadius: '4px !important',
					border: `1px solid ${dark ? '#4a4a4a' : '#d0d0d0'} !important`
				}
			},
			'& .multiselect': {
				minHeight: '38px !important',
				'& .multiselect__tags': {
					minHeight: '38px !important',
					padding: '6px 40px 0 8px !important',
					fontSize: '13px !important',
					borderRadius: '4px !important',
					border: `1px solid ${dark ? '#4a4a4a' : '#d0d0d0'} !important`
				},
				'& .multiselect__placeholder': {
					padding: '8px 0 !important',
					fontSize: '13px !important',
					marginBottom: '0 !important'
				},
				'& .multiselect__single': {
					padding: '8px 0 !important',
					fontSize: '13px !important',
					marginBottom: '0 !important',
					lineHeight: '22px !important'
				}
			}
		},
		tableContainer: {
			backgroundColor: dark ? '#3c3558' : '#ffffff',
			borderRadius: '4px',
			padding: '0',
			border: dark ? '1px solid #4a4a4a' : '1px solid #e0e0e0',
			overflow: 'hidden',
			'& .ReactTable': {
				border: 'none'
			},
			'& .rt-table': {
				border: 'none'
			}
		},
		tableStyles: {
			'& .rt-thead': {
				backgroundColor: '#E0F2F7 !important',
				'& .rt-th': {
					backgroundColor: '#E0F2F7 !important',
					color: '#333333 !important',
					fontWeight: '600',
					fontSize: '13px',
					padding: '10px 8px !important',
					borderRight: '1px solid #d0e0e5 !important',
					borderBottom: 'none !important',
					'&:last-child': {
						borderRight: 'none !important'
					}
				},
				'& .rt-th input': {
					backgroundColor: '#ffffff !important',
					border: '1px solid #d0e0e5 !important'
				}
			},
			'& .rt-tbody': {
				'& .rt-tr-group': {
					borderBottom: 'none !important',
					'&:nth-child(even) .rt-tr': {
						backgroundColor: '#f9f9f9 !important'
					},
					'&:nth-child(odd) .rt-tr': {
						backgroundColor: '#ffffff !important'
					},
					'& .rt-tr:hover': {
						backgroundColor: '#f0f8ff !important'
					},
					'& .rt-td': {
						padding: '10px 8px !important',
						fontSize: '13px',
						borderRight: 'none !important',
						borderBottom: 'none !important',
						textAlign: 'left !important'
					}
				}
			}
		},
		errorMessage: {
			color: '#dc3545',
			fontSize: '12px',
			marginTop: '4px',
			padding: '4px 8px',
			backgroundColor: dark ? 'rgba(220, 53, 69, 0.1)' : 'rgba(220, 53, 69, 0.05)',
			borderRadius: '3px',
			border: `1px solid ${dark ? 'rgba(220, 53, 69, 0.3)' : 'rgba(220, 53, 69, 0.2)'}`
		}
	};
};
const tablePaginationStyle = {
	display: 'flex',
	justifyContent: 'end',
	padding: '0px 15px',
	alignItems: 'baseline',
	'.MuiToolbar-root': {
		alignItems: 'baseline'
	}
};
const rowsPerPageOptions = [5, 10, 25, 50, 100];
export class Transactions extends Component {
	constructor(props) {
		super(props);
		this.state = {
			search: false,
			to: moment(),
			orgs: [],
			options: [],
			filtered: [],
			sorted: [],
			err: false,
			from: moment().subtract(1, 'days'),
			directLinkSearchResultsFlag: false,
			page: 0,
			rowsPerPage: 10,
			searchClick: false,
			queryFlag: false,
			defaultQuery: true
		};
	}

	componentDidMount() {
		const { getTransaction } = this.props;
		if (this.props.transactionId) {
			getTransaction('ChannelNotSpecified', this.props.transactionId);
			this.setState({ directLinkSearchResultsFlag: true });
		}
		// const { transactionList } = this.props;
		const selection = {};
		// transactionList.forEach(element => {
		// 	selection[element.blocknum] = false;
		// });
		const opts = [];
		this.props.transactionByOrg.forEach(val => {
			opts.push({ label: val.creator_msp_id, value: val.creator_msp_id });
		});
		this.setState({ selection, options: opts, defaultQuery: true });
		this.handleSearch();
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			this.state.search &&
			this.props.currentChannel !== prevProps.currentChannel
		) {
			if (this.interval !== undefined) {
				clearInterval(this.interval);
			}
			this.interval = setInterval(() => {
				this.searchTransactionList(this.props.currentChannel);
			}, 60000);
			this.searchTransactionList(this.props.currentChannel);
		}
		if (
			prevState.page != this.state.page ||
			prevState.rowsPerPage != this.state.rowsPerPage ||
			this.state.searchClick
		) {
			this.setState({ searchClick: false });
			this.handleSearch();
		}
	}
	componentWillUnmount() {
		clearInterval(this.interval);
		if (this.props.transactionId) {
			this.props.removeTransactionId();
		}
	}

	handleCustomRender(selected, options) {
		if (selected.length === 0) {
			return 'Select Orgs';
		}
		if (selected.length === options.length) {
			return 'All Orgs Selected';
		}

		return selected.join(',');
	}

	searchTransactionList = async channel => {
		let pageParams = { page: this.state.page + 1, size: this.state.rowsPerPage };
		let query = '';
		if (this.state.queryFlag) {
			query = this.state.from
				? `from=${new Date(this.state.from).toString()}&to=${new Date(
						this.state.to
				  ).toString()}`
				: ``;
			for (let i = 0; i < this.state.orgs.length; i++) {
				query += `&orgs=${this.state.orgs[i]}`;
			}
			this.setState({ queryFlag: false });
		} else if (this.state.defaultQuery) {
			query = '';
			this.setState({ defaultQuery: false });
		} else {
			query = this.props.transactionListSearchQuery;
		}
		let channelhash = this.props.currentChannel;
		if (channel !== undefined) {
			channelhash = channel;
		}
		await this.props.getTransactionListSearch(channelhash, query, pageParams);
	};

	handleTransactionClick = (e, txhash) => {
		e.preventDefault();
		if (txhash) {
			window.open(`#/transactions/${txhash}`, '_blank');
		}
	};

	handleMultiSelect = value => {
		this.setState({ orgs: value });
	};

	handleSearch = async () => {
		if (this.interval !== undefined) {
			clearInterval(this.interval);
		}
		this.interval = setInterval(() => {
			this.searchTransactionList();
		}, 60000);
		await this.searchTransactionList();
		this.setState({ search: true });
		if (this.props.transactionId) {
			this.setState({ directLinkSearchResultsFlag: false });
		}
	};

	handleClearSearch = () => {
		this.setState({
			to: moment(),
			orgs: [],
			err: false,
			from: moment().subtract(1, 'days')
		});
	};

	handleEye = (row, val) => {
		const { selection } = this.state;
		const data = Object.assign({}, selection, { [row.index]: !val });
		this.setState({ selection: data });
	};
	handlePageChange = (_e, page) => {
		this.setState({ page: page });
	};
	handleRowsChange = e => {
		this.setState({ page: 0, rowsPerPage: e.target.value });
	};

	render() {
		const { classes } = this.props;
		const columnHeaders = [
			{
				Header: 'Creator',
				accessor: 'creator_msp_id',
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['creator_msp_id'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			},
			{
				Header: 'Channel Name',
				accessor: 'channelname',
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['channelname'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			},
			{
				Header: 'Tx Id',
				accessor: 'txhash',
				className: classes.hash,
				Cell: row => (
					<span>
						<a
							data-command="transaction-partial-hash"
							className={classes.transactionLink}
							onClick={e => this.handleTransactionClick(e, row.value)}
							href={`#/transactions/${row.value}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{row.value ? `${row.value.slice(0, 8)}...${row.value.slice(-8)}` : '-'}
						</a>
					</span>
				),
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['txhash'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			},
			{
				Header: 'Type',
				accessor: 'type',
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['type'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			},
			{
				Header: 'Chaincode',
				accessor: 'chaincodename',
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['chaincodename'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			},
			{
				Header: 'Timestamp',
				accessor: 'createdt',
				filterMethod: (filter, rows) =>
					matchSorter(
						rows,
						filter.value,
						{ keys: ['createdt'] },
						{ threshold: matchSorter.rankings.SIMPLEMATCH }
					),
				filterAll: true
			}
		];
		/*
		const transactionList = this.state.search
			? this.props.transactionListSearch
			: this.props.transactionList;
		*/

		let transactionList;
		let noOfPages;
		if (this.props.transaction && this.state.directLinkSearchResultsFlag) {
			let tlArray = [{}];
			tlArray[0] = this.props.transaction;
			transactionList = tlArray;
		} else {
			transactionList = this.props.transactionListSearch;
			noOfPages = this.props.transactionListSearchTotalPages;
		}

		return (
			<div className={classes.container}>
				<div className={classes.filterSection}>
					<div className={classes.filterTitle}>TRANSACTION LIST</div>
					<div className={`${classes.filter} row searchRow`}>
					<div className={`${classes.filterElement} col-md-3`}>
						<label className="label">From</label>
						<DatePicker
							id="from"
							selected={this.state.from}
							showTimeSelect
							timeIntervals={5}
							maxDate={this.state.to}
							dateFormat="LLL"
							popperPlacement="bottom"
							popperModifiers={{
								flip: { behavior: ['bottom'] },
								preventOverflow: { enabled: false },
								hide: { enabled: false }
							}}
							onChange={date => {
								if (date > this.state.to) {
									this.setState({ err: true, from: date });
								} else {
									this.setState({ from: date, err: false });
								}
							}}
						/>
					</div>
					<div className={`${classes.filterElement} col-md-3`}>
						<label className="label">To</label>
						<DatePicker
							id="to"
							selected={this.state.to}
							showTimeSelect
							timeIntervals={5}
							minDate={this.state.from}
							dateFormat="LLL"
							popperPlacement="bottom"
							popperModifiers={{
								flip: { behavior: ['bottom'] },
								preventOverflow: { enabled: false },
								hide: { enabled: false }
							}}
							onChange={date => {
								if (date < this.state.from) {
									this.setState({ err: true, to: date });
								} else {
									this.setState({ to: date, err: false });
								}
							}}
						>
							{this.state.err && (
								<div className={classes.errorMessage}>
									From date should be less than To date
								</div>
							)}
						</DatePicker>
					</div>
					<div className={`${classes.filterElement} col-md-2`}>
						<label className="label">Select Orgs</label>
						<MultiSelect
							hasSelectAll
							valueRenderer={this.handleCustomRender}
							shouldToggleOnHover={false}
							selected={this.state.orgs}
							options={this.state.options}
							selectAllLabel="All Orgs"
							onSelectedChanged={value => {
								this.handleMultiSelect(value);
							}}
						/>
					</div>
					<div className={`${classes.filterElement} col-md-2`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.searchButton}
							color="primary"
							disabled={this.state.err || !this.state.from != !this.state.to}
							onClick={() => {
								this.setState({
									page: 0,
									searchClick: true,
									queryFlag: true,
									defaultQuery: false
								});
							}}
						>
							Search
						</Button>
					</div>
					<div className={`${classes.filterElement} col-md-1`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.filterButton}
							color="secondary"
							onClick={() => {
								this.handleClearSearch();
							}}
						>
							Reset
						</Button>
					</div>
					<div className={`${classes.filterElement} col-md-1`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.filterButton}
							color="secondary"
							onClick={() => this.setState({ filtered: [], sorted: [] })}
						>
							Clear Filter
						</Button>
					</div>
				</div>
				</div>
				<div className={`${classes.tableContainer} ${classes.tableStyles}`}>
				<ReactTable
					data={transactionList}
					columns={columnHeaders}
					pageSize={this.state.rowsPerPage}
					list
					filterable
					sorted={this.state.sorted}
					onSortedChange={sorted => {
						this.setState({ sorted });
					}}
					filtered={this.state.filtered}
					onFilteredChange={filtered => {
						this.setState({ filtered });
					}}
					minRows={0}
					style={{ height: '600px' }}
					showPaginationBottom={false}
				/>
				{transactionList.length > 0 && (
					<TablePagination
						page={this.state.page}
						sx={tablePaginationStyle}
						rowsPerPage={this.state.rowsPerPage}
						labelDisplayedRows={() => `Page ${this.state.page + 1} of ${noOfPages}`}
						rowsPerPageOptions={rowsPerPageOptions}
						onRowsPerPageChange={this.handleRowsChange}
						onPageChange={this.handlePageChange}
						backIconButtonProps={{
							disabled: this.state.page === 0
						}}
						nextIconButtonProps={{
							disabled: this.state.page + 1 === noOfPages
						}}
						className={classes.tablePagination}
						labelRowsPerPage={'Items per page'}
					/>
				)}
				</div>
			</div>
		);
	}
}

Transactions.propTypes = {
	currentChannel: currentChannelType.isRequired,
	getTransaction: getTransactionType.isRequired,
	transaction: transactionType,
	transactionList: transactionListType.isRequired
};

Transactions.defaultProps = {
	transaction: null
};

export default withStyles(styles)(Transactions);
